import React, { useState } from 'react';

const Input = ({ value, onChange, type = 'text', className = 'rb-input', ...rest }) => (
  <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)} className={className} {...rest} />
);

const Select = ({ value, onChange, options, className = 'rb-input' }) => (
  <select value={value ?? ''} onChange={e => onChange(e.target.value)} className={className}>
    <option value=""></option>
    {options.map(o => <option key={o} value={o}>{o}</option>)}
  </select>
);

const SecTitle = ({ children }) => <div className="rb-sec-title">{children}</div>;

const GridSection = ({ section, data, set }) => (
  <div className={`rb-section ${section.half ? 'half' : ''}`}>
    {section.title && <SecTitle>{section.title}</SecTitle>}
    <div className="rb-field-grid">
      {section.fields.map(f => (
        <div className="rb-field" key={f.key}>
          <label>{f.label}</label>
          {f.input === 'select'
            ? <Select value={data[f.key]} onChange={v => set(f.key, v)} options={f.options} />
            : <Input value={data[f.key]} onChange={v => set(f.key, v)} type={f.input === 'number' ? 'number' : f.input === 'date' ? 'date' : 'text'} />}
        </div>
      ))}
    </div>
  </div>
);

const LinesSection = ({ section, data, set }) => {
  const values = data[section.key] || [];
  return (
    <div className={`rb-section rb-lines ${section.half ? 'half' : ''}`}>
      <SecTitle>{section.title}</SecTitle>
      {Array.from({ length: section.count }).map((_, i) => (
        <div className="rb-line" key={i}>
          <span className="rb-line-num rb-serif">{i + 1}.</span>
          <Input value={values[i]} onChange={v => { const next = [...values]; next[i] = v; set(section.key, next); }} />
        </div>
      ))}
    </div>
  );
};

const AreaSection = ({ section, data, set }) => (
  <div className={`rb-section rb-area ${section.half ? 'half' : ''}`}>
    <SecTitle>{section.title}</SecTitle>
    <textarea rows={section.rows || 3} value={data[section.key] ?? ''} onChange={e => set(section.key, e.target.value)} />
  </div>
);

const ChecklistSection = ({ section, data, set }) => {
  const state = data[section.key] || {};
  const custom = state._custom || [];
  const [newItem, setNewItem] = useState('');
  const toggle = (item) => set(section.key, { ...state, [item]: !state[item] });
  const addItem = () => {
    const label = newItem.trim();
    if (!label) return;
    set(section.key, { ...state, _custom: [...custom, label] });
    setNewItem('');
  };
  const removeCustom = (label) => {
    const next = { ...state, _custom: custom.filter(c => c !== label) };
    delete next[label];
    set(section.key, next);
  };
  const allItems = [...section.items, ...custom];
  return (
    <div className={`rb-section ${section.half ? 'half' : ''}`}>
      <SecTitle>{section.title}</SecTitle>
      <div className="rb-checklist" style={{ gridTemplateColumns: `repeat(${section.columns || 1}, 1fr)` }}>
        {allItems.map(item => (
          <label className="rb-check" key={item}>
            <input type="checkbox" checked={!!state[item]} onChange={() => toggle(item)} />
            <span className="rb-check-label">{item}</span>
            {custom.includes(item) && <button className="rb-check-remove rb-no-print" onClick={(e) => { e.preventDefault(); removeCustom(item); }}>✕</button>}
          </label>
        ))}
      </div>
      {section.allowAdd && (
        <div className="rb-check-add rb-no-print">
          <Input value={newItem} onChange={setNewItem} placeholder="Add a task..."
            onKeyDown={e => { if (e.key === 'Enter') addItem(); }} />
          <button className="rb-btn" onClick={addItem} data-testid={`add-${section.key}`}>Add</button>
        </div>
      )}
    </div>
  );
};

const TableSection = ({ section, data, set }) => {
  const seed = section.seedRows ? section.seedRows.map(r => ({ ...r })) : Array.from({ length: section.minRows || 3 }).map(() => ({}));
  const rows = data[section.key] || seed;
  const update = (i, key, v) => {
    const next = rows.map((r, idx) => idx === i ? { ...r, [key]: v } : r);
    set(section.key, next);
  };
  const addRow = () => set(section.key, [...rows, {}]);
  const removeRow = (i) => set(section.key, rows.filter((_, idx) => idx !== i));
  const isLow = (r) => section.lowStock && r.current !== undefined && r.current !== '' && r.min !== undefined && r.min !== '' && parseFloat(r.current) < parseFloat(r.min);
  return (
    <div className="rb-section">
      <SecTitle>{section.title}</SecTitle>
      <table className="rb-table">
        <thead>
          <tr>{section.columns.map(c => <th key={c.key} style={{ width: c.width }}>{c.label}</th>)}<th className="rb-no-print" style={{ width: '3%' }}></th></tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={isLow(row) ? 'rb-low' : ''}>
              {section.columns.map(c => (
                <td key={c.key}>
                  {c.calc ? <span className="rb-calc-cell">${c.calc(row).toFixed(2)}</span>
                    : c.input === 'check' ? (
                      <label className="rb-check" style={{ justifyContent: 'center' }}>
                        <input type="checkbox" checked={!!row[c.key]} onChange={() => update(i, c.key, !row[c.key])} />
                      </label>
                    ) : c.input === 'select' ? <Select className="rb-cell-input" value={row[c.key]} onChange={v => update(i, c.key, v)} options={c.options} />
                    : <Input className="rb-cell-input" value={row[c.key]} onChange={v => update(i, c.key, v)} type={c.input === 'number' ? 'number' : c.input === 'date' ? 'date' : 'text'} />}
                </td>
              ))}
              <td className="rb-no-print"><button className="rb-row-remove" onClick={() => removeRow(i)}>✕</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <button className="rb-btn rb-add-row rb-no-print" onClick={addRow} data-testid={`add-row-${section.key}`}>+ Add Row</button>
    </div>
  );
};

const SalesSummary = ({ section, data }) => {
  const rows = data[section.key] || [];
  const total = r => (parseFloat(r.qty) || 0) * (parseFloat(r.price) || 0);
  const now = new Date();
  const today = now.toISOString().slice(0, 10);
  const monthPrefix = today.slice(0, 7);
  const weekAgo = new Date(now.getTime() - 6 * 86400000).toISOString().slice(0, 10);
  const sum = (filter) => rows.filter(filter).reduce((s, r) => s + total(r), 0);
  const units = rows.reduce((s, r) => s + (parseInt(r.qty) || 0), 0);
  const byVariety = {};
  rows.forEach(r => { if (r.product) byVariety[r.product] = (byVariety[r.product] || 0) + total(r); });
  return (
    <div className="rb-section">
      <SecTitle>{section.title}</SecTitle>
      <div className="rb-summary-grid">
        <div className="rb-summary-box"><div className="rb-sum-label">Today</div><div className="rb-sum-value" data-testid="sum-today">${sum(r => r.date === today).toFixed(2)}</div></div>
        <div className="rb-summary-box"><div className="rb-sum-label">This Week</div><div className="rb-sum-value" data-testid="sum-week">${sum(r => r.date >= weekAgo && r.date <= today).toFixed(2)}</div></div>
        <div className="rb-summary-box"><div className="rb-sum-label">This Month</div><div className="rb-sum-value" data-testid="sum-month">${sum(r => (r.date || '').startsWith(monthPrefix)).toFixed(2)}</div></div>
        <div className="rb-summary-box"><div className="rb-sum-label">All Sales</div><div className="rb-sum-value" data-testid="sum-all">${sum(() => true).toFixed(2)}</div></div>
        <div className="rb-summary-box"><div className="rb-sum-label">Units Sold</div><div className="rb-sum-value" data-testid="sum-units">{units}</div></div>
      </div>
      {Object.keys(byVariety).length > 0 && (
        <div className="rb-summary-grid" style={{ marginTop: '2mm' }}>
          {Object.entries(byVariety).map(([v, amt]) => (
            <div className="rb-summary-box" key={v}><div className="rb-sum-label">{v}</div><div className="rb-sum-value">${amt.toFixed(2)}</div></div>
          ))}
        </div>
      )}
    </div>
  );
};

const Followups = ({ section, data }) => {
  const rows = (data[section.key] || []).filter(r => r.followup && !['Stockist', 'Not Proceeding'].includes(r.status));
  const sorted = [...rows].sort((a, b) => (a.followup || '').localeCompare(b.followup || ''));
  return (
    <div className="rb-section">
      <SecTitle>{section.title}</SecTitle>
      {sorted.length === 0
        ? <p className="rb-followup-empty">No follow-ups due — add a follow-up date to a stockist below.</p>
        : sorted.map((r, i) => (
          <div className="rb-followup-item" key={i}>
            <span><strong>{r.business || 'Unnamed'}</strong>{r.contact ? ` — ${r.contact}` : ''} <em style={{ color: 'var(--olive)' }}>{r.status || ''}</em></span>
            <span style={{ fontWeight: 700 }}>{r.followup && new Date(r.followup + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</span>
          </div>
        ))}
    </div>
  );
};

const SocialSummary = ({ section, data }) => {
  const rows = data[section.key] || [];
  const statuses = ['Idea', 'To Create', 'Ready', 'Scheduled', 'Posted'];
  return (
    <div className="rb-section">
      <SecTitle>{section.title}</SecTitle>
      <div className="rb-summary-grid">
        {statuses.map(s => (
          <div className="rb-summary-box" key={s}>
            <div className="rb-sum-label">{s}</div>
            <div className="rb-sum-value">{rows.filter(r => r.status === s).length}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const Section = ({ section, data, set }) => {
  switch (section.type) {
    case 'grid': return <GridSection section={section} data={data} set={set} />;
    case 'lines': return <LinesSection section={section} data={data} set={set} />;
    case 'area': return <AreaSection section={section} data={data} set={set} />;
    case 'checklist': return <ChecklistSection section={section} data={data} set={set} />;
    case 'table': return <TableSection section={section} data={data} set={set} />;
    case 'salesSummary': return <SalesSummary section={section} data={data} />;
    case 'followups': return <Followups section={section} data={data} />;
    case 'socialSummary': return <SocialSummary section={section} data={data} />;
    default: return null;
  }
};
